import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { initials } from "@/lib/format";
import { UserCircle, Loader2, IdCard, Briefcase, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const ROLE_LABEL = { super_admin: "Super Admin", manager: "Manager", employee: "Employee" };

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ job_title: "", job_description: "", id_number: "", email_notifications: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({
      job_title: user.job_title || "",
      job_description: user.job_description || "",
      id_number: user.id_number || "",
      email_notifications: user.email_notifications !== false,
    });
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/me/profile", form);
      await refresh();
      toast.success("Profile updated.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!user) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <div className="flex items-center gap-2"><UserCircle className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">My profile</h1></div>

      <div className="card-emerald p-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 grid place-items-center font-display text-xl font-bold text-emerald-300">{initials(user.name)}</div>
        <div>
          <p className="font-display text-lg font-semibold">{user.name}</p>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          <Badge className="mt-1 bg-emerald-500/15 text-emerald-300">{ROLE_LABEL[user.role] || user.role}</Badge>
        </div>
      </div>

      <div className="card-emerald p-6 space-y-4">
        <p className="text-sm text-muted-foreground">Add your job details so your team and managers know your role. These are your own to edit.</p>

        <div>
          <Label className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Job title / role</Label>
          <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            placeholder="e.g. Warehouse Supervisor" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="profile-job-title-input" />
        </div>

        <div>
          <Label>Job description</Label>
          <Textarea rows={4} value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })}
            placeholder="Briefly describe what you do day to day…" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="profile-job-description-input" />
        </div>

        <div>
          <Label className="flex items-center gap-1.5"><IdCard className="w-3.5 h-3.5" /> ID number</Label>
          <Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })}
            placeholder="e.g. staff or national ID number" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="profile-id-number-input" />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="profile-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save profile"}
          </Button>
        </div>
      </div>

      <div className="card-emerald p-6" data-testid="notif-pref-card">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-2">
            <Mail className="w-4 h-4 text-emerald-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Email notifications</p>
              <p className="text-xs text-muted-foreground">Get emails for announcements, meeting invites and chat messages you miss while offline.</p>
            </div>
          </div>
          <Switch checked={form.email_notifications} onCheckedChange={(v) => setForm({ ...form, email_notifications: v })} data-testid="email-notif-switch" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Press “Save profile” above to apply changes.</p>
      </div>
    </div>
  );
}
